import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { PageHeader, Loading, ErrorState } from "@/components/common";
import { brl, fmtDate, fmtDateTime, pct } from "@/lib/format";
import { corEtapa } from "@/lib/stageColors";
import { ChevronRight, Download, Loader2, ArrowLeft, Home, FileSpreadsheet, Info } from "@/components/icons";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const INPUT = "h-10 px-3 rounded-lg border border-input bg-white text-sm";

export default function RelatorioGerencial() {
  const now = new Date();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [year, setYear] = useState(Number(sp.get("year") || now.getFullYear()));
  const [month, setMonth] = useState(Number(sp.get("month") || now.getMonth() + 1));
  const [diretorId, setDiretorId] = useState(sp.get("diretor_id") || "");
  const [gerenteId, setGerenteId] = useState(sp.get("gerente_id") || "");
  const [vendedorId, setVendedorId] = useState(sp.get("vendedor_id") || "");
  const [data, setData] = useState(null);
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [exporting, setExporting] = useState(false);

  const syncURL = useCallback((next) => {
    const params = new URLSearchParams(sp);
    params.set("year", String(next.year ?? year));
    params.set("month", String(next.month ?? month));
    const d = next.diretor_id !== undefined ? next.diretor_id : diretorId;
    const g = next.gerente_id !== undefined ? next.gerente_id : gerenteId;
    const v = next.vendedor_id !== undefined ? next.vendedor_id : vendedorId;
    if (d) params.set("diretor_id", d); else params.delete("diretor_id");
    if (g) params.set("gerente_id", g); else params.delete("gerente_id");
    if (v) params.set("vendedor_id", v); else params.delete("vendedor_id");
    setSp(params, { replace: false });
  }, [year, month, diretorId, gerenteId, vendedorId, setSp, sp]);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const params = new URLSearchParams({ year: String(year), month: String(month) });
      if (diretorId) params.set("diretor_id", diretorId);
      if (gerenteId) params.set("gerente_id", gerenteId);
      if (vendedorId) params.set("vendedor_id", vendedorId);
      // R-2 (item 1c): remove `tree` do array de deps. Antes, a cascata
      // (load → setTree → rerender → load) fazia DUAS chamadas em sequência
      // ao /reports/gerencial toda vez que a aba abria.
      const [r, h] = await Promise.all([
        api.get(`/reports/gerencial?${params}`),
        tree ? Promise.resolve({ data: tree }) : api.get("/hierarquia"),
      ]);
      setData(r.data); if (!tree) setTree(h.data);
    } catch { setError(true); } finally { setLoading(false); }
  }, [year, month, diretorId, gerenteId, vendedorId]);
  useEffect(() => { load(); }, [load]);

  const setDiretor = (id) => { setDiretorId(id); setGerenteId(""); setVendedorId(""); syncURL({ diretor_id: id, gerente_id: "", vendedor_id: "" }); };
  const setGerente = (id) => { setGerenteId(id); setVendedorId(""); syncURL({ gerente_id: id, vendedor_id: "" }); };
  const setVendedor = (id) => { setVendedorId(id); syncURL({ vendedor_id: id }); };
  const voltar = () => {
    if (vendedorId) setVendedor("");
    else if (gerenteId) setGerente("");
    else if (diretorId) setDiretor("");
    else navigate(-1);
  };

  const gerentesDoDiretor = () => {
    if (!tree || !diretorId) return [];
    const d = (tree.diretores || tree.gerentes ? (tree.diretores || []) : []).find((x) => x.id === diretorId);
    return d ? d.gerentes.filter((g) => g.id) : [];
  };
  const vendedoresDoGerente = () => {
    if (!tree) return [];
    if (tree.gerentes) {
      // manager logado
      const g = tree.gerentes.find((x) => x.id === gerenteId || !gerenteId);
      return g ? g.vendedores : [];
    }
    for (const d of tree.diretores || []) {
      for (const g of d.gerentes) if (g.id === gerenteId) return g.vendedores;
    }
    return [];
  };

  const exportPDF = () => {
    if (!data) return;
    setExporting(true);
    try {
      const doc = new jsPDF({ orientation: "landscape", format: "a4" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const escopo = data.crumbs?.vendedor?.name || data.crumbs?.gerente?.name || data.crumbs?.diretor?.name || "Empresa";
      const escopoTipo = data.crumbs?.vendedor ? "Vendedor" : data.crumbs?.gerente ? "Gerente" : data.crumbs?.diretor ? "Diretor" : "Empresa (visão consolidada)";
      const periodo = `${MESES[month - 1]}/${year}`;

      // -------- Cabeçalho (capa curta) --------
      doc.setFillColor(0, 62, 98);
      doc.rect(0, 0, pageW, 22, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(15); doc.setFont("helvetica", "bold");
      doc.text("Relatório Gerencial", 14, 10);
      doc.setFontSize(10); doc.setFont("helvetica", "normal");
      doc.text(`${escopo} · ${periodo}`, 14, 16);
      doc.setFontSize(8);
      doc.text(`Gerado em ${fmtDateTime(new Date().toISOString())}`, pageW - 14, 10, { align: "right" });
      doc.text(escopoTipo, pageW - 14, 16, { align: "right" });
      doc.setTextColor(0, 0, 0);

      // -------- Indicadores consolidados (Total equipe) --------
      const total = (data.resumo || []).find((r) => !r.vendedor_id) || null;
      const vendedoresCount = (data.resumo || []).filter((r) => r.vendedor_id).length;
      let y = 28;
      if (total) {
        doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(0, 62, 98);
        doc.text("Indicadores consolidados do período", 14, y);
        doc.setDrawColor(230); doc.line(14, y + 1.5, pageW - 14, y + 1.5);
        y += 6;
        const kpis = [
          ["Vendedores no escopo", String(vendedoresCount)],
          ["Oportunidades abertas", `${total.abertas_qtd} · ${brl(total.abertas_valor)}`],
          ["Previsão ponderada", brl(total.ponderado)],
          ["Ganhos no período", `${total.ganhos_qtd} · ${brl(total.ganhos_valor)}`],
          ["Perdidos no período", `${total.perdidos_qtd} · ${brl(total.perdidos_valor)}`],
          ["Conversão (fechados)", total.conversao == null ? "—" : pct(total.conversao, 1)],
          ["Clientes prospectados", String(total.clientes_novos)],
          ["Atividades concluídas", String(total.atividades_concluidas)],
          ["Atividades atrasadas", String(total.atrasadas_hoje)],
          ["Oportunidades paradas", String(total.paradas)],
          ["Sem próxima ação", String(total.sem_proxima_acao)],
          ["Solicitações em aberto", String(total.cobrancas_abertas)],
        ];
        const colW = (pageW - 28) / 4;
        const rowH = 12;
        kpis.forEach((k, i) => {
          const cx = 14 + (i % 4) * colW;
          const cy = y + Math.floor(i / 4) * rowH;
          doc.setDrawColor(230); doc.setFillColor(248, 250, 252);
          doc.roundedRect(cx, cy, colW - 3, rowH - 2, 1.5, 1.5, "FD");
          doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(100);
          doc.text(k[0], cx + 3, cy + 4);
          doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(0, 62, 98);
          doc.text(k[1], cx + 3, cy + 8.5);
        });
        y += Math.ceil(kpis.length / 4) * rowH + 2;
      }
      doc.setTextColor(0, 0, 0);

      // -------- Tabela: Desempenho por vendedor --------
      doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(0, 62, 98);
      doc.text("Desempenho por vendedor", 14, y + 2);
      doc.setTextColor(0, 0, 0);
      autoTable(doc, {
        startY: y + 4,
        head: [["Vendedor", "Abertas (qtd · R$)", "Ponderado (R$)", "Ganhos (qtd · R$)", "Perdidos (qtd · R$)", "Conv.%", "Clientes novos", "Ativ. concl.", "Atras.", "Paradas", "Sem próx.", "Solic. abertas", "Tempo méd. resp."]],
        body: (data.resumo || []).map((r) => [
          r.vendedor,
          `${r.abertas_qtd} · ${brl(r.abertas_valor)}`,
          brl(r.ponderado),
          `${r.ganhos_qtd} · ${brl(r.ganhos_valor)}`,
          `${r.perdidos_qtd} · ${brl(r.perdidos_valor)}`,
          r.conversao == null ? "—" : pct(r.conversao, 1), r.clientes_novos, r.atividades_concluidas, r.atrasadas_hoje, r.paradas, r.sem_proxima_acao,
          r.cobrancas_abertas, r.tempo_medio_resposta_horas != null ? `${r.tempo_medio_resposta_horas}h` : "—",
        ]),
        styles: { fontSize: 7, cellPadding: 1.6 },
        headStyles: { fillColor: [0, 138, 176], textColor: 255, fontStyle: "bold" },
        didParseCell: (h) => {
          if (h.row.raw && h.row.raw[0] === "TOTAL EQUIPE") {
            h.cell.styles.fontStyle = "bold";
            h.cell.styles.fillColor = [232, 240, 248];
          }
        },
      });

      // -------- Tabela: Pipeline por etapa --------
      if ((data.pipeline_por_etapa || []).length) {
        const y2 = (doc.lastAutoTable?.finalY || 40) + 6;
        doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(0, 62, 98);
        doc.text("Pipeline por etapa (oportunidades ativas)", 14, y2);
        doc.setTextColor(0, 0, 0);
        const totalPipe = data.pipeline_por_etapa.reduce((s, x) => s + (x.count || 0), 0);
        const totalPipeValor = data.pipeline_por_etapa.reduce((s, x) => s + (x.valor || 0), 0);
        autoTable(doc, {
          startY: y2 + 2,
          head: [["Etapa", "Quantidade", "% do total", "Valor (R$)", "% do valor"]],
          body: data.pipeline_por_etapa.map((s) => [
            s.stage, s.count,
            totalPipe ? pct((s.count / totalPipe) * 100, 1) : "0%",
            brl(s.valor),
            totalPipeValor ? pct((s.valor / totalPipeValor) * 100, 1) : "0%",
          ]).concat([[
            { content: "TOTAL", styles: { fontStyle: "bold" } },
            { content: totalPipe, styles: { fontStyle: "bold" } },
            { content: "100%", styles: { fontStyle: "bold" } },
            { content: brl(totalPipeValor), styles: { fontStyle: "bold" } },
            { content: "100%", styles: { fontStyle: "bold" } },
          ]]),
          styles: { fontSize: 8 }, headStyles: { fillColor: [0, 62, 98], textColor: 255 },
        });
      }

      // -------- Ampliação de mix (ex "Cross-selling", se ativo) --------
      const cs = data.cross_selling;
      if (cs?.available && (cs.abertas_count > 0 || cs.ganhos_count > 0)) {
        const y3 = (doc.lastAutoTable?.finalY || 40) + 6;
        doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(0, 62, 98);
        doc.text("Ampliação de mix — oportunidades marcadas", 14, y3);
        doc.setTextColor(0, 0, 0);
        autoTable(doc, {
          startY: y3 + 2,
          head: [["Situação", "Quantidade", "Valor (R$)"]],
          body: [
            ["Abertas no escopo", cs.abertas_count, brl(cs.abertas_valor)],
            ["Ganhos no período", cs.ganhos_count, brl(cs.ganhos_valor)],
          ],
          styles: { fontSize: 8 }, headStyles: { fillColor: [0, 138, 176], textColor: 255 },
        });
        if (cs.items?.length) {
          autoTable(doc, {
            startY: (doc.lastAutoTable?.finalY || 40) + 2,
            head: [["Cliente", "Título", "Vendedor", "Etapa", "Valor (R$)"]],
            body: cs.items.map((it) => [it.cliente_nome || "—", it.titulo || "—", it.owner_name || "—", it.stage || "—", brl(it.valor)]),
            styles: { fontSize: 7 }, headStyles: { fillColor: [80, 100, 120], textColor: 255 },
          });
        }
      }

      // -------- Faturamento vs Meta + Comparativos (mês/trimestre/ano) --------
      const cf = data.comparativos_faturamento;
      const fv = data.faturamento_por_vendedor || [];
      const varTxt = (v) => {
        if (!v) return "—";
        if (v.variacao_pct == null) return v.novo ? "Novo" : "—";
        const sig = v.variacao_pct >= 0 ? "▲" : "▼";
        return `${sig} ${pct(Math.abs(v.variacao_pct), 1)}`;
      };
      if (cf) {
        doc.addPage();
        doc.setFillColor(0, 62, 98); doc.rect(0, 0, pageW, 14, "F");
        doc.setTextColor(255, 255, 255); doc.setFontSize(12); doc.setFont("helvetica", "bold");
        doc.text("Faturamento e comparativos", 14, 9);
        doc.setFontSize(8); doc.setFont("helvetica", "normal");
        doc.text(`${escopo} · base: ${periodo}`, pageW - 14, 9, { align: "right" });
        doc.setTextColor(0, 0, 0);

        // Meta vs Realizado no mês do relatório
        const atingMes = cf.meta_mes ? (cf.mes.atual / cf.meta_mes) * 100 : 0;
        const atingTri = cf.meta_trimestre ? (cf.trimestre.atual / cf.meta_trimestre) * 100 : 0;
        const atingAno = cf.meta_ano ? (cf.ano.atual / cf.meta_ano) * 100 : 0;
        doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(0, 62, 98);
        doc.text("Meta × Realizado (mês do relatório, trimestre e ano até o mês)", 14, 22);
        doc.setTextColor(0, 0, 0);
        autoTable(doc, {
          startY: 24,
          head: [["Base", "Meta (R$)", "Faturado (R$)", "% da meta", "Falta para meta"]],
          body: [
            [`Mês (${periodo})`, brl(cf.meta_mes), brl(cf.mes.atual), pct(atingMes, 1), brl(Math.max(0, (cf.meta_mes || 0) - (cf.mes.atual || 0)))],
            ["Trimestre acumulado", brl(cf.meta_trimestre), brl(cf.trimestre.atual), pct(atingTri, 1), brl(Math.max(0, (cf.meta_trimestre || 0) - (cf.trimestre.atual || 0)))],
            [`Ano acumulado (Jan..${MESES[month - 1]})`, brl(cf.meta_ano), brl(cf.ano.atual), pct(atingAno, 1), brl(Math.max(0, (cf.meta_ano || 0) - (cf.ano.atual || 0)))],
          ],
          styles: { fontSize: 8 }, headStyles: { fillColor: [0, 138, 176], textColor: 255 },
        });

        // Comparativos vs período anterior
        const y5 = (doc.lastAutoTable?.finalY || 40) + 6;
        doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(0, 62, 98);
        doc.text("Comparativos: atual vs. período anterior", 14, y5);
        doc.setTextColor(0, 0, 0);
        autoTable(doc, {
          startY: y5 + 2,
          head: [["Comparativo", "Período atual (R$)", "Período anterior (R$)", "Variação", "Diferença (R$)"]],
          body: [
            [`Mês (${periodo} vs. mês anterior)`, brl(cf.mes.atual), brl(cf.mes.anterior), varTxt(cf.mes), brl(cf.mes.atual - cf.mes.anterior)],
            ["Trimestre atual vs. trimestre anterior", brl(cf.trimestre.atual), brl(cf.trimestre.anterior), varTxt(cf.trimestre), brl(cf.trimestre.atual - cf.trimestre.anterior)],
            [`Ano (Jan..${MESES[month - 1]}/${year} vs. mesmo período de ${year - 1})`, brl(cf.ano.atual), brl(cf.ano.anterior), varTxt(cf.ano), brl(cf.ano.atual - cf.ano.anterior)],
          ],
          styles: { fontSize: 8 }, headStyles: { fillColor: [0, 62, 98], textColor: 255 },
          didParseCell: (h) => {
            if (h.section === "body" && h.column.index === 3) {
              const raw = String(h.cell.raw || "");
              if (raw.startsWith("▲")) h.cell.styles.textColor = [22, 130, 80];
              else if (raw.startsWith("▼")) h.cell.styles.textColor = [180, 40, 40];
            }
          },
        });

        // Faturamento por vendedor no mês + variação vs mês anterior + ano acumulado
        if (fv.length) {
          const y6 = (doc.lastAutoTable?.finalY || 40) + 6;
          doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(0, 62, 98);
          doc.text("Faturamento por vendedor", 14, y6);
          doc.setTextColor(0, 0, 0);
          const totFatMes = fv.reduce((s, r) => s + r.faturado_mes, 0);
          const totMeta = fv.reduce((s, r) => s + r.meta_mes, 0);
          const totMesAnt = fv.reduce((s, r) => s + r.faturado_mes_anterior, 0);
          const totFatAno = fv.reduce((s, r) => s + r.faturado_ano, 0);
          const totAnoAnt = fv.reduce((s, r) => s + r.faturado_ano_anterior, 0);
          autoTable(doc, {
            startY: y6 + 2,
            head: [["Vendedor", `Meta ${MESES[month - 1]} (R$)`, `Faturado ${MESES[month - 1]} (R$)`, "% meta", "Faturado mês anterior", "Var. mês", `Ano até ${MESES[month - 1]}`, "Ano anterior (mesmo período)", "Var. ano"]],
            body: fv.map((r) => [
              r.vendedor,
              brl(r.meta_mes), brl(r.faturado_mes), pct(r.atingimento_mes, 1),
              brl(r.faturado_mes_anterior), varTxt(r.variacao_mes),
              brl(r.faturado_ano), brl(r.faturado_ano_anterior), varTxt(r.variacao_ano),
            ]).concat([[
              { content: "TOTAL EQUIPE", styles: { fontStyle: "bold" } },
              { content: brl(totMeta), styles: { fontStyle: "bold" } },
              { content: brl(totFatMes), styles: { fontStyle: "bold" } },
              { content: totMeta ? pct((totFatMes / totMeta) * 100, 1) : "0%", styles: { fontStyle: "bold" } },
              { content: brl(totMesAnt), styles: { fontStyle: "bold" } },
              { content: varTxt(cf.mes), styles: { fontStyle: "bold" } },
              { content: brl(totFatAno), styles: { fontStyle: "bold" } },
              { content: brl(totAnoAnt), styles: { fontStyle: "bold" } },
              { content: varTxt(cf.ano), styles: { fontStyle: "bold" } },
            ]]),
            styles: { fontSize: 7, cellPadding: 1.6 },
            headStyles: { fillColor: [0, 138, 176], textColor: 255 },
            didParseCell: (h) => {
              if (h.section === "body" && (h.column.index === 5 || h.column.index === 8)) {
                const raw = String(h.cell.raw?.content || h.cell.raw || "");
                if (raw.startsWith("▲")) h.cell.styles.textColor = [22, 130, 80];
                else if (raw.startsWith("▼")) h.cell.styles.textColor = [180, 40, 40];
              }
            },
          });
        }
      }

      // -------- Tabela: Cota Real por Gestor --------
      const cog = data.cota_oficial_por_gestor || [];
      if (cog.length) {
        doc.addPage();
        doc.setFillColor(0, 62, 98); doc.rect(0, 0, pageW, 14, "F");
        doc.setTextColor(255, 255, 255); doc.setFontSize(12); doc.setFont("helvetica", "bold");
        doc.text("Cota Real por Gestor", 14, 9);
        doc.setFontSize(8); doc.setFont("helvetica", "normal");
        doc.text(`Acumulado em ${year} até ${MESES[month - 1]}`, pageW - 14, 9, { align: "right" });
        doc.setTextColor(0, 0, 0);
        autoTable(doc, {
          startY: 22,
          head: [["Gestor", "Período", "Cota real (R$)", "Meta distribuída (R$)", "Gordura %", "Faturado (R$)", "% da cota real", "% da meta distribuída"]],
          body: cog.flatMap((r) => {
            const mesLabel = MESES[(r.mes_sel || month) - 1];
            const sgn = (v) => v == null ? "—" : `${v >= 0 ? "+" : ""}${pct(v, 1)}`;
            const p = (v) => v == null ? "—" : pct(v, 1);
            return [
              [`${r.nome}\n${r.cargo}`, `${mesLabel} ${year}`,
               brl(r.cota_mes), brl(r.meta_mes), sgn(r.gordura_mes_pct),
               brl(r.faturado_mes), p(r.pct_cota_oficial_mes), p(r.pct_meta_distribuida_mes)],
              ["", `Acumulado até ${mesLabel}`,
               brl(r.cota_oficial_ytd), brl(r.meta_distribuida_ytd), sgn(r.gordura_pct),
               brl(r.faturado_ytd), p(r.pct_cota_oficial), p(r.pct_meta_distribuida)],
            ];
          }),
          styles: { fontSize: 8, cellPadding: 1.8 },
          headStyles: { fillColor: [0, 138, 176], textColor: 255 },
        });
      }

      // -------- Por vendedor (detalhes) --------
      const dados = data.por_vendedor || [];
      dados.forEach((v) => {
        doc.addPage();
        // faixa colorida com o nome do vendedor
        doc.setFillColor(0, 62, 98);
        doc.rect(0, 0, pageW, 14, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12); doc.setFont("helvetica", "bold");
        doc.text(v.vendedor || "—", 14, 9);
        doc.setFontSize(8); doc.setFont("helvetica", "normal");
        doc.text(`${escopo} · ${periodo}`, pageW - 14, 9, { align: "right" });
        doc.setTextColor(0, 0, 0);

        const r = (data.resumo || []).find((x) => x.vendedor_id === v.vendedor_id);
        // linha de KPIs do vendedor
        let yy = 20;
        if (r) {
          const mini = [
            ["Abertas", `${r.abertas_qtd} · ${brl(r.abertas_valor)}`],
            ["Ponderado", brl(r.ponderado)],
            ["Ganhos", `${r.ganhos_qtd} · ${brl(r.ganhos_valor)}`],
            ["Perdidos", `${r.perdidos_qtd} · ${brl(r.perdidos_valor)}`],
            ["Conv.%", r.conversao == null ? "—" : `${r.conversao}%`],
            ["Novos", String(r.clientes_novos)],
            ["Atv. concl.", String(r.atividades_concluidas)],
            ["Atrasadas", String(r.atrasadas_hoje)],
          ];
          const cw = (pageW - 28) / mini.length;
          mini.forEach((k, i) => {
            const cx = 14 + i * cw;
            doc.setDrawColor(230); doc.setFillColor(248, 250, 252);
            doc.roundedRect(cx, yy, cw - 2, 10, 1, 1, "FD");
            doc.setFontSize(6.5); doc.setTextColor(100); doc.setFont("helvetica", "normal");
            doc.text(k[0], cx + 2, yy + 3.5);
            doc.setFontSize(8.5); doc.setTextColor(0, 62, 98); doc.setFont("helvetica", "bold");
            doc.text(k[1], cx + 2, yy + 8);
          });
          yy += 14;
          doc.setTextColor(0, 0, 0);
        }

        const secao = (titulo, head, body, headColor = [0, 138, 176]) => {
          if (!body.length) return;
          const startY = (doc.lastAutoTable?.finalY || yy) + 3;
          doc.setFontSize(9); doc.setFont("helvetica", "bold"); doc.setTextColor(0, 62, 98);
          doc.text(`${titulo}  (${body.length})`, 14, startY);
          doc.setTextColor(0, 0, 0);
          autoTable(doc, {
            startY: startY + 2,
            head: [head], body,
            styles: { fontSize: 7, cellPadding: 1.4 },
            headStyles: { fillColor: headColor, textColor: 255, fontStyle: "bold" },
          });
        };

        // reset lastAutoTable para a primeira seção iniciar a partir do yy
        doc.lastAutoTable = { finalY: yy };

        secao("Oportunidades em andamento",
          ["Cliente", "Título", "Etapa", "Valor (R$)", "Prob.", "Previsão", "D.etapa", "Últ. interação", "Próxima ação", "Data próx."],
          (v.em_andamento || []).map((d) => [
            d.cliente_nome || "—", d.titulo || "—", d.stage || "—", brl(d.valor),
            `${d.prob || 0}%`, fmtDate(d.data_prevista),
            d.dias_na_etapa == null ? "—" : String(d.dias_na_etapa),
            d.ultima_interacao ? fmtDate(d.ultima_interacao) : "—",
            d.proxima_acao || "—",
            d.proxima_data ? fmtDate(d.proxima_data) : "—",
          ]));

        secao("Clientes prospectados no período",
          ["Cliente", "Data do cadastro", "Origem"],
          (v.prospectados || []).map((c) => [c.cliente_nome || "—", fmtDate(c.created_at), c.origem || "—"]));

        secao("Oportunidades paradas (>7d sem interação ou sem próxima ação)",
          ["Cliente", "Título", "Etapa", "Valor (R$)", "Dias parada", "Sem próxima ação"],
          (v.paradas || []).map((d) => [
            d.cliente_nome || "—", d.titulo || "—", d.stage || "—", brl(d.valor),
            d.dias_parada == null ? "—" : String(d.dias_parada),
            d.sem_proxima_acao ? "Sim" : "Não",
          ]),
          [220, 130, 50]);

        secao("Ganhos no período",
          ["Cliente", "Título", "Valor (R$)", "Data"],
          (v.ganhos || []).map((d) => [d.cliente_nome || "—", d.titulo || "—", brl(d.valor), fmtDate(d.data)]),
          [22, 130, 80]);

        secao("Perdidos no período",
          ["Cliente", "Título", "Valor (R$)", "Data", "Motivo"],
          (v.perdidos || []).map((d) => [d.cliente_nome || "—", d.titulo || "—", brl(d.valor), fmtDate(d.data), d.motivo || "—"]),
          [180, 40, 40]);

        if ((v.perdas_por_motivo || []).length) {
          const totalP = v.perdas_por_motivo.reduce((s, m) => s + m.qtd, 0);
          secao("Perdas por motivo",
            ["Motivo", "Quantidade", "% das perdas"],
            v.perdas_por_motivo.map((m) => [m.motivo, m.qtd, totalP ? pct((m.qtd / totalP) * 100, 1) : "0%"]),
            [150, 60, 60]);
        }

        // Se o vendedor não teve nada no período, deixa um aviso claro
        const semDados = !(v.em_andamento?.length || v.prospectados?.length || v.paradas?.length || v.ganhos?.length || v.perdidos?.length);
        if (semDados) {
          doc.setFontSize(9); doc.setTextColor(120);
          doc.text("Sem movimentações registradas para este vendedor no período.", 14, (doc.lastAutoTable?.finalY || yy) + 8);
          doc.setTextColor(0, 0, 0);
        }
      });

      // -------- Glossário (última página) --------
      doc.addPage();
      doc.setFillColor(0, 62, 98);
      doc.rect(0, 0, pageW, 14, "F");
      doc.setTextColor(255, 255, 255); doc.setFontSize(12); doc.setFont("helvetica", "bold");
      doc.text("Glossário e critérios de cálculo", 14, 9);
      doc.setTextColor(0, 0, 0);
      const glossario = [
        ["Faturamento", "Líquido Sankhya (vendas − devoluções) do escopo no período — mesma fonte do quadro azul da Início e do painel Faturamento."],
        ["Meta", "Soma de quotas_history.goal lançada no editor de cota, no período correspondente."],
        ["% da meta", "Faturado ÷ Meta × 100 no mesmo período (mês/trimestre/ano)."],
        ["Comparativo do mês", "Mês do relatório vs. mês imediatamente anterior."],
        ["Comparativo do trimestre", "Trimestre em curso do mês do relatório vs. trimestre anterior (mesmos 3 meses)."],
        ["Comparativo do ano", "Janeiro até o mês do relatório vs. mesmo período do ano anterior (YTD)."],
        ["Variação (▲/▼)", "(Atual − Anterior) ÷ Anterior × 100. \"Novo\" quando o período anterior era zero."],
        ["Abertas", "Oportunidades cujo estágio ≠ Fechado e ≠ Perdido no fim do período."],
        ["Ponderado", "Σ (valor da oportunidade × probabilidade %) para as oportunidades abertas."],
        ["Ganhos", "Oportunidades movidas para Fechado dentro do período (stage_updated_at ∈ [ini, fim))."],
        ["Perdidos", "Oportunidades movidas para Perdido dentro do período (lost_at/stage_updated_at ∈ [ini, fim))."],
        ["Conv.%", "Ganhos ÷ (Ganhos + Perdidos) do período × 100. 0% quando não há fechamentos no período."],
        ["Clientes novos", "Clientes cadastrados pelo vendedor dentro do período."],
        ["Atv. concluídas", "Atividades marcadas como concluídas dentro do período."],
        ["Atrasadas", "Atividades ainda não concluídas com data anterior a hoje."],
        ["Paradas", "Oportunidades abertas sem interação registrada há > 7 dias OU sem próxima ação agendada."],
        ["Sem próx. ação", "Oportunidades abertas sem nenhuma atividade pendente vinculada."],
        ["Solic. abertas", "Solicitações (comentários com kind=cobranca) ainda não respondidas."],
        ["Tempo méd. resposta", "Média em horas entre a solicitação e a resposta do responsável, quando respondida."],
        ["Ampliação de mix", "Oportunidades em clientes que já são da MPires (ex 'cross-selling')."],
      ];
      autoTable(doc, {
        startY: 20,
        head: [["Métrica", "Definição"]],
        body: glossario,
        styles: { fontSize: 8, cellPadding: 2.2 },
        headStyles: { fillColor: [0, 138, 176], textColor: 255 },
        columnStyles: { 0: { fontStyle: "bold", cellWidth: 42 }, 1: { cellWidth: "auto" } },
      });

      // -------- Rodapé com paginação --------
      const total_pages = doc.getNumberOfPages();
      for (let i = 1; i <= total_pages; i++) {
        doc.setPage(i);
        doc.setFontSize(8); doc.setTextColor(140);
        doc.text(`MPires Connect · Relatório Gerencial · ${escopo} · ${periodo}`, 14, pageH - 6);
        doc.text(`Página ${i} de ${total_pages}`, pageW - 14, pageH - 6, { align: "right" });
      }

      doc.save(`relatorio-gerencial-${escopo.replace(/\s+/g, "_")}-${year}-${String(month).padStart(2, "0")}.pdf`);
    } catch (err) { toast.error(errMsg(err) || "Falha ao exportar"); }
    finally { setExporting(false); }
  };

  const exportXLSX = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ year: String(year), month: String(month), format: "xlsx" });
      if (diretorId) params.set("diretor_id", diretorId);
      if (gerenteId) params.set("gerente_id", gerenteId);
      if (vendedorId) params.set("vendedor_id", vendedorId);
      const r = await api.get(`/reports/gerencial/export?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      const escopo = data?.crumbs?.vendedor?.name || data?.crumbs?.gerente?.name || data?.crumbs?.diretor?.name || "Empresa";
      a.download = `relatorio-gerencial-${escopo.replace(/\s+/g, "_")}-${year}-${String(month).padStart(2, "0")}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (err) { toast.error(errMsg(err) || "Falha ao exportar Excel"); }
    finally { setExporting(false); }
  };

  if (loading) return <Loading />;
  if (error) return <ErrorState onRetry={load} />;

  const resumo = data?.resumo || [];
  const porVendedor = data?.por_vendedor || [];
  const crumbs = data?.crumbs || {};
  const diretores = tree?.diretores || [];

  return (
    <div>
      {/* Breadcrumb */}
      <div className="flex items-center gap-1 text-sm text-muted-foreground mb-3" data-testid="gerencial-crumbs">
        <button onClick={() => { setDiretor(""); }} className="flex items-center gap-1 hover:text-navy"><Home className="w-3.5 h-3.5" /> Empresa</button>
        {crumbs.diretor ? <><ChevronRight className="w-3.5 h-3.5" /><button onClick={() => setGerente("")} className="hover:text-navy" data-testid="crumb-diretor">{crumbs.diretor.name}</button></> : null}
        {crumbs.gerente ? <><ChevronRight className="w-3.5 h-3.5" /><button onClick={() => setVendedor("")} className="hover:text-navy" data-testid="crumb-gerente">{crumbs.gerente.name}</button></> : null}
        {crumbs.vendedor ? <><ChevronRight className="w-3.5 h-3.5" /><span className="text-navy font-medium" data-testid="crumb-vendedor">{crumbs.vendedor.name}</span></> : null}
      </div>

      <PageHeader
        title="Relatório gerencial"
        subtitle={`${crumbs.vendedor?.name || crumbs.gerente?.name || crumbs.diretor?.name || "Empresa"} · ${MESES[month - 1]}/${year}`}
        action={
          <div className="hidden lg:flex items-center gap-2 flex-wrap">
            <button onClick={voltar} data-testid="gerencial-voltar" className="flex items-center gap-1.5 h-10 px-3 rounded-lg border border-input text-sm font-medium hover:bg-slate-50"><ArrowLeft className="w-4 h-4" /> Voltar</button>
            <button onClick={exportXLSX} disabled={exporting || !porVendedor.length} data-testid="export-xlsx-btn" className="flex items-center gap-2 h-10 px-4 rounded-lg border border-input text-navy text-sm font-semibold hover:bg-slate-50 disabled:opacity-60">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Excel
            </button>
            <button onClick={exportPDF} disabled={exporting || !porVendedor.length} data-testid="export-pdf-btn" className="flex items-center gap-2 h-10 px-4 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover disabled:opacity-60">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} PDF
            </button>
          </div>
        }
      />

      {/* Item 1.5 — em telas < 1024px, os botões de exportação saem do cabeçalho e ocupam bloco próprio */}
      <div className="lg:hidden grid grid-cols-2 gap-2 mb-3" data-testid="gerencial-export-actions-mobile">
        <button onClick={exportPDF} disabled={exporting || !porVendedor.length} data-testid="export-pdf-btn-mobile"
          className="flex items-center justify-center gap-2 h-11 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover transition-colors disabled:opacity-60">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Exportar PDF
        </button>
        <button onClick={exportXLSX} disabled={exporting || !porVendedor.length} data-testid="export-xlsx-btn-mobile"
          className="flex items-center justify-center gap-2 h-11 rounded-lg border border-input text-navy text-sm font-semibold hover:bg-slate-50 disabled:opacity-60">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Exportar Excel
        </button>
      </div>

      {/* Seletores rápidos */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="gerencial-filtros">
        <select className={INPUT} value={month} onChange={(e) => { setMonth(Number(e.target.value)); syncURL({ month: Number(e.target.value) }); }} data-testid="ger-mes">
          {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
        <select className={INPUT} value={year} onChange={(e) => { setYear(Number(e.target.value)); syncURL({ year: Number(e.target.value) }); }} data-testid="ger-ano">
          {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => <option key={y}>{y}</option>)}
        </select>
        {diretores.length > 0 && (
          <select className={INPUT} value={diretorId} onChange={(e) => setDiretor(e.target.value)} data-testid="ger-diretor">
            <option value="">Todos os diretores</option>
            {diretores.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        )}
        {(diretorId || tree?.gerentes) && (
          <select className={INPUT} value={gerenteId} onChange={(e) => setGerente(e.target.value)} data-testid="ger-gerente">
            <option value="">Todos os gerentes</option>
            {(tree?.gerentes || gerentesDoDiretor()).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        )}
        {(gerenteId || tree?.gerentes) && (
          <select className={INPUT} value={vendedorId} onChange={(e) => setVendedor(e.target.value)} data-testid="ger-vendedor">
            <option value="">Todos os vendedores</option>
            {vendedoresDoGerente().map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        )}
      </div>

      {/* Sem drill: cards de diretores para navegação (visível quando é DG/admin sem filtros) */}
      {!diretorId && diretores.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6" data-testid="gerencial-cards-diretores">
          {diretores.map((d) => (
            <button key={d.id} onClick={() => setDiretor(d.id)} data-testid={`card-diretor-${d.id}`} className="text-left bg-card rounded-2xl border border-black/5 p-5 hover:border-brand/40 transition-colors">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Diretor</div>
              <div className="text-lg font-semibold text-navy mt-1">{d.name}</div>
              <div className="text-xs text-muted-foreground mt-2">{d.gerentes.filter((g) => g.id).length} gerentes · {d.gerentes.reduce((s, g) => s + g.vendedores.length, 0)} vendedores</div>
            </button>
          ))}
        </div>
      )}

      <section className="mb-8">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Resumo</h2>
        {/* Item 3.10 — em telas < md a tabela vira cards para não estourar a tela */}
        <div className="md:hidden space-y-3">
          {resumo.length === 0 && (
            <div className="rounded-xl border border-black/5 bg-white p-4 text-center text-sm text-muted-foreground">Sem vendedores no escopo.</div>
          )}
          {resumo.map((r, i) => {
            const isTotal = !r.vendedor_id;
            const meta = r.meta || 0;
            const pctAting = meta > 0 ? Math.round((r.realizado || 0) / meta * 100) : null;
            return (
              <div key={r.vendedor_id || i}
                onClick={() => r.vendedor_id && setVendedor(r.vendedor_id)}
                data-testid={`resumo-card-${r.vendedor_id || "total"}`}
                className={`rounded-xl border p-4 ${isTotal ? "border-brand/40 bg-brand-soft/20" : "border-black/5 bg-white active:bg-slate-50"}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className={`truncate ${isTotal ? "font-semibold text-navy" : "font-medium text-navy"}`}>{r.vendedor}</div>
                    <div className="text-[11px] text-muted-foreground">{r.abertas_qtd} abertas · {r.ganhos_qtd} ganhas · {r.perdidos_qtd} perdidas</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Realizado</div>
                    <div className="text-sm font-semibold text-navy" data-testid={`realizado-${r.vendedor_id || "total"}`}>{brl(r.realizado || 0)}</div>
                  </div>
                </div>
                {pctAting !== null && (
                  <div className="mt-2">
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-brand" style={{ width: `${pctAting}%` }} />
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1">{pctAting}% da meta ({brl(meta)})</div>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-2 mt-3 text-[11px]">
                  <div>
                    <div className="uppercase tracking-wider text-muted-foreground">Ganho CRM</div>
                    <div className="text-ok font-medium">{brl(r.ganhos_valor)}</div>
                  </div>
                  <div>
                    <div className="uppercase tracking-wider text-muted-foreground">Ponderado</div>
                    <div className="text-navy font-medium">{brl(r.ponderado)}</div>
                  </div>
                  <div>
                    <div className="uppercase tracking-wider text-muted-foreground">Conv.</div>
                    <div className="text-navy font-medium">{r.conversao == null ? "—" : `${r.conversao}%`}</div>
                  </div>
                </div>
                {(r.atrasadas_hoje || r.paradas || r.sem_proxima_acao || r.cobrancas_abertas) > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {r.atrasadas_hoje > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-bad-soft text-bad font-medium">Atras. {r.atrasadas_hoje}</span>}
                    {r.paradas > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-warn-soft text-warn font-medium">Paradas {r.paradas}</span>}
                    {r.sem_proxima_acao > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">S/prox {r.sem_proxima_acao}</span>}
                    {r.cobrancas_abertas > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-soft text-brand-hover font-medium">Solic. {r.cobrancas_abertas}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="hidden md:block overflow-x-auto rounded-2xl border border-black/5 bg-white">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-muted-foreground bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2">Vendedor</th>
                <th className="text-right px-3 py-2">Abertas</th>
                <th className="text-right px-3 py-2">Ponderado</th>
                <th className="text-right px-3 py-2" title="Soma do valor das oportunidades Fechado no período">Ganho no CRM</th>
                <th className="text-right px-3 py-2" title="Realizado: líquido Sankhya do mês (vendas − devoluções)">Realizado</th>
                <th className="text-right px-3 py-2" title="Meta cadastrada no mês (quotas_history.goal)">Meta</th>
                <th className="text-right px-3 py-2" title="Realizado ÷ Meta × 100">% Ating.</th>
                <th className="text-right px-3 py-2">Perdidos</th>
                <th className="text-right px-3 py-2" title="Ganhas ÷ (Ganhas + Perdidas) fechadas no período × 100">Conv.</th>
                <th className="text-right px-3 py-2">Clientes</th>
                <th className="text-right px-3 py-2">Atv.</th>
                <th className="text-right px-3 py-2">Atras.</th>
                <th className="text-right px-3 py-2">Paradas</th>
                <th className="text-right px-3 py-2">S/prox</th>
                <th className="text-right px-3 py-2">Solic.</th>
                <th className="text-right px-3 py-2">Tempo</th>
              </tr>
            </thead>
            <tbody>
              {resumo.map((r, i) => (
                <tr key={r.vendedor_id || i}
                    className={r.vendedor_id ? "border-t border-black/5 cursor-pointer hover:bg-slate-50" : "border-t-2 border-brand/40 bg-brand-soft/20 font-semibold"}
                    onClick={() => r.vendedor_id && setVendedor(r.vendedor_id)}
                    data-testid={`resumo-${r.vendedor_id || "total"}`}>
                  <td className="text-left px-3 py-2 text-navy">{r.vendedor}</td>
                  <td className="text-right px-3 py-2">{r.abertas_qtd} · {brl(r.abertas_valor)}</td>
                  <td className="text-right px-3 py-2">{brl(r.ponderado)}</td>
                  <td className="text-right px-3 py-2 text-ok">{r.ganhos_qtd} · {brl(r.ganhos_valor)}</td>
                  <td className="text-right px-3 py-2 text-navy font-semibold" data-testid={`realizado-${r.vendedor_id || "total"}`}>{brl(r.realizado || 0)}</td>
                  <td className="text-right px-3 py-2 text-slate-600" data-testid={`meta-${r.vendedor_id || "total"}`}>{brl(r.meta || 0)}</td>
                  <td className="text-right px-3 py-2 text-navy" data-testid={`ating-${r.vendedor_id || "total"}`}>{(r.meta || 0) > 0 ? `${Math.round((r.realizado || 0) / r.meta * 100)}%` : "—"}</td>
                  <td className="text-right px-3 py-2 text-bad">{r.perdidos_qtd} · {brl(r.perdidos_valor)}</td>
                  <td className="text-right px-3 py-2">{r.conversao == null ? "—" : `${r.conversao}%`}</td>
                  <td className="text-right px-3 py-2">{r.clientes_novos}</td>
                  <td className="text-right px-3 py-2">{r.atividades_concluidas}</td>
                  <td className="text-right px-3 py-2 text-bad">{r.atrasadas_hoje}</td>
                  <td className="text-right px-3 py-2">{r.paradas}</td>
                  <td className="text-right px-3 py-2">{r.sem_proxima_acao}</td>
                  <td className="text-right px-3 py-2">{r.cobrancas_abertas}</td>
                  <td className="text-right px-3 py-2">{r.tempo_medio_resposta_horas != null ? `${r.tempo_medio_resposta_horas}h` : "—"}</td>
                </tr>
              ))}
              {resumo.length === 0 && (
                <tr><td colSpan={16} className="px-3 py-6 text-center text-muted-foreground">Sem vendedores no escopo.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {(data?.cota_oficial_por_gestor || []).length > 0 && (
        <section className="mb-6 rounded-2xl border border-black/5 bg-white p-5" data-testid="gerencial-cota-oficial">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-1">Cota real por Gestor</h2>
          <p className="text-xs text-muted-foreground mb-3">Acumulado em {year} até {MESES[month - 1]} · gestor + vendedores sob ele.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[920px]" data-testid="cota-oficial-tabela">
              <thead className="text-xs uppercase tracking-wider text-muted-foreground bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2">Gestor</th>
                  <th className="text-right px-3 py-2" title="Cota anual definida pela diretoria">Cota real</th>
                  <th className="text-right px-3 py-2" title="Soma das cotas dos vendedores do gestor (anual)">Meta distribuída</th>
                  <th className="text-right px-3 py-2" title="(Meta distribuída ÷ Cota real − 1) × 100 — folga acima da cota">Gordura %</th>
                  <th className="text-right px-3 py-2" title="Líquido Sankhya acumulado no ano até o mês do relatório">Faturado</th>
                  <th className="text-right px-3 py-2" title="Faturado ÷ Cota real (YTD) × 100">% da cota real</th>
                  <th className="text-right px-3 py-2" title="Faturado ÷ Meta distribuída (YTD) × 100">% da meta distribuída</th>
                </tr>
              </thead>
              <tbody>
                {data.cota_oficial_por_gestor.map((r) => {
                  const sgnMes = (v) => v == null ? "—" : `${v >= 0 ? "+" : ""}${pct(v, 1)}`;
                  const sgnAcc = (v) => v == null ? "—" : `${v >= 0 ? "+" : ""}${pct(v, 1)}`;
                  const mesLabel = MESES[(r.mes_sel || month) - 1];
                  return [
                    (
                      <tr key={`${r.gestor_id}-mes`} className="border-t border-black/5 hover:bg-slate-50" data-testid={`cota-oficial-row-${r.gestor_id}-mes`}>
                        <td className="text-left px-3 py-2">
                          <div className="font-medium text-navy">{r.nome}</div>
                          <div className="text-[11px] text-muted-foreground">{r.cargo} · {mesLabel} {year}</div>
                        </td>
                        <td className="text-right px-3 py-2 text-navy">{brl(r.cota_mes)}</td>
                        <td className="text-right px-3 py-2 text-slate-600">{brl(r.meta_mes)}</td>
                        <td className="text-right px-3 py-2 text-navy">{sgnMes(r.gordura_mes_pct)}</td>
                        <td className="text-right px-3 py-2 text-navy font-semibold">{brl(r.faturado_mes)}</td>
                        <td className="text-right px-3 py-2 text-navy">{r.pct_cota_oficial_mes == null ? "—" : pct(r.pct_cota_oficial_mes, 1)}</td>
                        <td className="text-right px-3 py-2 text-navy">{r.pct_meta_distribuida_mes == null ? "—" : pct(r.pct_meta_distribuida_mes, 1)}</td>
                      </tr>
                    ),
                    (
                      <tr key={`${r.gestor_id}-acc`} className="border-t border-black/5 bg-slate-50/40" data-testid={`cota-oficial-row-${r.gestor_id}`}>
                        <td className="text-left px-3 py-2">
                          <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Acumulado até {mesLabel}</div>
                        </td>
                        <td className="text-right px-3 py-2 text-navy">{brl(r.cota_oficial_ytd)}</td>
                        <td className="text-right px-3 py-2 text-slate-600">{brl(r.meta_distribuida_ytd)}</td>
                        <td className="text-right px-3 py-2 text-navy">{sgnAcc(r.gordura_pct)}</td>
                        <td className="text-right px-3 py-2 text-navy font-semibold">{brl(r.faturado_ytd)}</td>
                        <td className="text-right px-3 py-2 text-navy">{r.pct_cota_oficial == null ? "—" : pct(r.pct_cota_oficial, 1)}</td>
                        <td className="text-right px-3 py-2 text-navy">
                          {r.pct_meta_distribuida == null ? "—" : pct(r.pct_meta_distribuida, 1)}
                          {r.meta_desde_mes && r.meta_desde_mes > 1 ? (
                            <div className="text-[10px] text-muted-foreground">desde {MESES[r.meta_desde_mes - 1].slice(0, 3)}/{String(year).slice(-2)}</div>
                          ) : null}
                        </td>
                      </tr>
                    ),
                  ];
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {(data?.pipeline_por_etapa || []).length > 0 && (
        <section className="mb-6 rounded-2xl border border-black/5 bg-white p-5" data-testid="gerencial-pipeline">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Pipeline por etapa</h2>
          <div className="space-y-2">
            {(() => {
              const max = Math.max(...data.pipeline_por_etapa.map((s) => s.count)) || 1;
              return data.pipeline_por_etapa.map((s) => (
                <div key={s.stage} className="flex items-center gap-3 text-sm">
                  <span className="w-32 shrink-0 text-navy">{s.stage}</span>
                  <div className="flex-1 h-5 bg-slate-100 rounded overflow-hidden">
                    <div className="h-full" style={{ width: `${(s.count / max) * 100}%`, backgroundColor: corEtapa(s.stage) }} />
                  </div>
                  <span className="w-10 text-right font-semibold text-navy">{s.count}</span>
                  <span className="w-28 text-right text-muted-foreground">{brl(s.valor)}</span>
                </div>
              ));
            })()}
          </div>
        </section>
      )}

      {data?.cross_selling && data.cross_selling.available && (data.cross_selling.abertas_count > 0 || data.cross_selling.ganhos_count > 0) ? (
        <section className="mb-6 rounded-2xl border border-black/5 bg-white p-5" data-testid="gerencial-cross-selling">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">Ampliação de mix</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div className="rounded-xl bg-slate-50 p-3">
              <div className="text-xs text-muted-foreground">Abertas</div>
              <div className="text-lg font-semibold text-navy">{data.cross_selling.abertas_count}</div>
            </div>
            <div className="rounded-xl bg-slate-50 p-3">
              <div className="text-xs text-muted-foreground">Valor aberto</div>
              <div className="text-lg font-semibold text-navy">{brl(data.cross_selling.abertas_valor)}</div>
            </div>
            <div className="rounded-xl bg-ok-soft p-3">
              <div className="text-xs text-ok/80">Ganhos no período</div>
              <div className="text-lg font-semibold text-ok">{data.cross_selling.ganhos_count}</div>
            </div>
            <div className="rounded-xl bg-ok-soft p-3">
              <div className="text-xs text-ok/80">Valor ganho</div>
              <div className="text-lg font-semibold text-ok">{brl(data.cross_selling.ganhos_valor)}</div>
            </div>
          </div>
          {data.cross_selling.items?.length ? (
            <div className="overflow-x-auto rounded-lg border border-black/5">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-muted-foreground bg-slate-50"><tr>
                  <th className="text-left px-3 py-2">Cliente</th><th className="text-left px-3 py-2">Título</th>
                  <th className="text-left px-3 py-2">Vendedor</th><th className="text-left px-3 py-2">Etapa</th>
                  <th className="text-right px-3 py-2">Valor</th>
                </tr></thead>
                <tbody>
                  {data.cross_selling.items.map((it) => (
                    <tr key={it.id} className="border-t border-black/5"><td className="px-3 py-1.5">{it.cliente_nome || "—"}</td><td className="px-3 py-1.5">{it.titulo}</td><td className="px-3 py-1.5">{it.owner_name || "—"}</td><td className="px-3 py-1.5">{it.stage}</td><td className="text-right px-3 py-1.5">{brl(it.valor)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      ) : null}

      {porVendedor.map((v) => (
        <section key={v.vendedor_id} className="mb-10 rounded-2xl border border-black/5 bg-white p-5" data-testid={`vendedor-${v.vendedor_id}`}>
          <h3 className="text-lg font-semibold text-navy mb-3">{v.vendedor}</h3>
          <ListaTabela title="Oportunidades em andamento" items={v.em_andamento} cols={[
            ["Cliente", (d) => d.cliente_nome || "—"],
            ["Título", (d) => d.titulo],
            ["Etapa", (d) => d.stage],
            ["Valor", (d) => brl(d.valor)],
            ["Prob.", (d) => `${d.prob}%`],
            ["Previsão", (d) => fmtDate(d.data_prevista)],
            ["D.etapa", (d) => (d.dias_na_etapa == null ? "—" : d.dias_na_etapa)],
            ["Últ. int.", (d) => d.ultima_interacao ? fmtDate(d.ultima_interacao) : "—"],
            ["Próx. ação", (d) => d.proxima_acao || "—"],
          ]} />
          <ListaTabela title="Clientes prospectados no período" items={v.prospectados} cols={[
            ["Cliente", (c) => c.cliente_nome || "—"],
            ["Cadastro", (c) => fmtDate(c.created_at)],
            ["Origem", (c) => c.origem || "—"],
          ]} />
          <ListaTabela title="Paradas (>7d sem interação ou sem próxima ação)" items={v.paradas} cols={[
            ["Cliente", (d) => d.cliente_nome || "—"],
            ["Título", (d) => d.titulo],
            ["Etapa", (d) => d.stage],
            ["Valor", (d) => brl(d.valor)],
            ["Dias parada", (d) => (d.dias_parada == null ? "—" : d.dias_parada)],
            ["Sem próx.", (d) => d.sem_proxima_acao ? "Sim" : "—"],
          ]} />
          <ListaTabela title="Ganhos no período" items={v.ganhos} cols={[
            ["Cliente", (d) => d.cliente_nome || "—"],
            ["Título", (d) => d.titulo],
            ["Valor", (d) => brl(d.valor)],
            ["Data", (d) => fmtDate(d.data)],
          ]} />
          <ListaTabela title="Perdidos no período" items={v.perdidos} cols={[
            ["Cliente", (d) => d.cliente_nome || "—"],
            ["Título", (d) => d.titulo],
            ["Valor", (d) => brl(d.valor)],
            ["Data", (d) => fmtDate(d.data)],
            ["Motivo", (d) => d.motivo || "—"],
          ]} />
          {v.perdas_por_motivo.length ? (
            <div className="mt-3 text-sm">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Perdas por motivo</div>
              <div className="flex flex-wrap gap-2">
                {v.perdas_por_motivo.map((m) => (
                  <span key={m.motivo} className="text-xs bg-slate-100 rounded-full px-2 py-1 text-slate-700">{m.motivo} · {m.qtd}</span>
                ))}
              </div>
            </div>
          ) : null}
        </section>
      ))}
    </div>
  );
}

function ListaTabela({ title, items, cols }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{title} <span className="text-slate-400 font-normal">({items.length})</span></div>
      <div className="overflow-x-auto rounded-lg border border-black/5">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-muted-foreground bg-slate-50">
            <tr>{cols.map(([h]) => <th key={h} className="text-left px-3 py-2 whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={it.id || i} className="border-t border-black/5">
                {cols.map(([h, fn]) => <td key={h} className="px-3 py-1.5 text-slate-700 whitespace-nowrap">{fn(it)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
